import { motion } from "motion/react";

import { isDefined } from "@/helpers/util";

interface SpeedometerProps {
  /**
   * Number between 0 and 1
   */
  score: number;
  min?: number;
  max?: number;
  renderCurrentValue?: (score: number | undefined) => string | undefined;
  withDelay?: boolean;
}

const MIN_ROTATION_DEG = -90;
const MAX_ROTATION_DEG = 90;
const TOTAL_ROTATION_DEG = MAX_ROTATION_DEG - MIN_ROTATION_DEG;

export function Speedometer({
  score,
  min = 0,
  max = 10,
  renderCurrentValue,
  withDelay,
}: SpeedometerProps): React.ReactNode {
  // Used to compute the rotation of the pointer
  let snappedScore = score;
  // Bit hacky but this makes it so 6 and 8 are just in the next segment
  if (snappedScore > 0.5 && snappedScore < 0.98) {
    snappedScore += 0.01;
  }

  const rotation = isDefined(snappedScore) ? MIN_ROTATION_DEG + TOTAL_ROTATION_DEG * snappedScore : 0;

  return (
    <figure className="relative flex w-full items-end gap-space-sm">
      <span className="text-body leading-none">{min}</span>
      <svg xmlns="http://www.w3.org/2000/svg" width="100%" fill="none" viewBox="0 0 160 80">
        <path
          className="fill-chart-score-very-high"
          d="M157.274 59.294a79.998 79.998 0 00-26.928-41.466L115.242 36.48a56 56 0 0118.85 29.026l23.182-6.212z"
        />
        <path
          className="fill-chart-score-very-high"
          d="M156 80c2.209 0 4.01-1.792 3.9-3.998a80.01 80.01 0 00-8.705-32.49c-1.008-1.965-3.464-2.617-5.377-1.512l-13.856 8c-1.914 1.105-2.555 3.545-1.59 5.533a55.997 55.997 0 015.485 20.47C136.015 78.207 137.791 80 140 80h16z"
        />
        <path
          className="fill-chart-score-high"
          d="M144.718 32.972a79.999 79.999 0 00-40.002-29.058L97.301 26.74a56 56 0 0128.002 20.34l19.415-14.108z"
        />
        <path
          className="fill-chart-score-midpoint"
          d="M104.721 3.915a79.999 79.999 0 00-49.442 0l7.416 22.826a56 56 0 0134.61 0l7.416-22.826z"
        />
        <path
          className="fill-chart-score-low"
          d="M55.279 3.915a80 80 0 00-40 29.062l19.416 14.107a56 56 0 0128-20.343L55.279 3.915z"
        />
        <path
          className="fill-chart-score-very-low"
          d="M18.515 35.328c-1.788-1.298-4.298-.907-5.506.943A80 80 0 00.1 76c-.11 2.208 1.69 4 3.9 4h16c2.21 0 3.985-1.793 4.143-3.997a56.001 56.001 0 018.318-25.602c1.168-1.875.785-4.37-1.002-5.668l-12.944-9.405z"
        />
        <path
          className="fill-chart-score-very-low"
          d="M15.279 32.977a80 80 0 00-14.84 38.66l23.868 2.51a56 56 0 0110.388-27.063L15.28 32.977z"
        />
        {Boolean(snappedScore) && (
          <motion.path
            className="speedometer-pointer-origin fill-text-primary"
            initial={{
              rotate: MIN_ROTATION_DEG,
              opacity: 0,
            }}
            animate={{
              rotate: rotation,
              opacity: 1,
            }}
            transition={{
              delay: withDelay ? 0.2 : 0.1,
              duration: 0.8,
              ease: [0.61, 0.04, 0.3, 0.99],
            }}
            key={rotation}
            fillRule="evenodd"
            d="M80,16 L87,28 H73 L80,16 Z"
            clipRule="evenodd"
          />
        )}
        {renderCurrentValue && (
          <text
            x="50%"
            y="99%"
            textAnchor="middle"
            fontFamily="var(--font-family-sans)"
            fontWeight="var(--font-weight-bold)"
            fontSize={36}
            fill="var(--color-text-primary)"
          >
            {renderCurrentValue(score) ?? "-"}
          </text>
        )}
      </svg>
      <span className="text-body leading-none">{max}</span>
    </figure>
  );
}
